import { createContext, useContext, useReducer, useEffect, ReactNode } from 'react'
import {
  User as FirebaseUser,
  signInWithPopup,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth'
import { auth } from '@/lib/firebase'
import type { AuthContextType, AuthState, AuthUser } from '@/types/auth'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

const QA_BYPASS = import.meta.env.VITE_QA_BYPASS === 'true'
const QA_MOCK_USER: AuthUser = {
  user_id: 'qa-user',
  email: 'qa@buildgravity.com',
  name: 'QA User',
  role: 'admin',
  permissions: ['read', 'write', 'admin'],
  tenant_id: 'buildgravity',
  tenant: { id: 'buildgravity', name: 'BuildGravity' },
  available_tenants: [],
  modules: { sales: true, sales_dialer: true },
  is_active: true,
  is_admin: true,
  is_super_admin: false,
  last_login: new Date().toISOString(),
}

// Auth reducer
type AuthAction =
  | { type: 'AUTH_LOADING' }
  | { type: 'AUTH_SUCCESS'; payload: AuthUser }
  | { type: 'AUTH_ERROR'; payload: string }
  | { type: 'AUTH_SIGNOUT' }
  | { type: 'CLEAR_ERROR' }

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_LOADING':
      return { ...state, isLoading: true, error: null }
    case 'AUTH_SUCCESS':
      return {
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      }
    case 'AUTH_ERROR':
      return {
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      }
    case 'AUTH_SIGNOUT':
      return {
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      }
    case 'CLEAR_ERROR':
      return { ...state, error: null }
    default:
      return state
  }
}

const initialState: AuthState = QA_BYPASS
  ? { user: QA_MOCK_USER, isAuthenticated: true, isLoading: false, error: null }
  : { user: null, isAuthenticated: false, isLoading: true, error: null }

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const useHasDialer = () => {
  const { user } = useAuth()
  return user?.modules?.sales_dialer === true
}

export const useCanWrite = () => {
  const { user } = useAuth()
  return user?.modules?.sales === true
}

export const useIsViewer = () => {
  const { user } = useAuth()
  return user?.role === 'viewer'
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [state, dispatch] = useReducer(authReducer, initialState)

  // Fetch user profile from backend
  const fetchUserProfile = async (firebaseUser: FirebaseUser): Promise<AuthUser | null> => {
    try {
      const token = await firebaseUser.getIdToken()
      const response = await fetch(`${API_BASE_URL}/api/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch user profile: ${response.statusText}`)
      }

      const data = await response.json()

      // Backend returns { user: {...}, timestamp: ... }
      const user = data.user

      // Transform backend response to AuthUser format
      const authUser: AuthUser = {
        user_id: user.user_id,
        email: user.email,
        name: user.name,
        role: user.role,
        permissions: user.permissions || [],
        tenant_id: user.tenant_id,
        tenant: user.tenant || { id: user.tenant_id, name: user.tenant_name || user.tenant_id },
        available_tenants: user.available_tenants || [],
        modules: user.modules,
        is_active: user.is_active ?? true,
        is_admin: user.is_admin ?? false,
        is_super_admin: user.is_super_admin ?? false,
        last_login: data.timestamp || new Date().toISOString(),
      }

      return authUser
    } catch (error) {
      console.error('Error fetching user profile:', error)
      throw error
    }
  }

  // Listen to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          dispatch({ type: 'AUTH_LOADING' })
          const authUser = await fetchUserProfile(firebaseUser)
          if (authUser) {
            dispatch({ type: 'AUTH_SUCCESS', payload: authUser })
          } else {
            dispatch({ type: 'AUTH_ERROR', payload: 'Failed to load user profile' })
          }
        } catch (error) {
          dispatch({
            type: 'AUTH_ERROR',
            payload: error instanceof Error ? error.message : 'Authentication failed'
          })
        }
      } else {
        dispatch({ type: 'AUTH_SIGNOUT' })
      }
    })

    return () => unsubscribe()
  }, [])

  // Sign in with email/password
  const signInWithEmail = async (email: string, password: string) => {
    try {
      dispatch({ type: 'AUTH_LOADING' })
      await signInWithEmailAndPassword(auth, email, password)
      // onAuthStateChanged will handle the rest
    } catch (error) {
      dispatch({
        type: 'AUTH_ERROR',
        payload: error instanceof Error ? error.message : 'Sign in failed',
      })
      throw error
    }
  }

  // Sign in with Google
  const signIn = async () => {
    try {
      dispatch({ type: 'AUTH_LOADING' })
      const provider = new GoogleAuthProvider()
      await signInWithPopup(auth, provider)
      // onAuthStateChanged will handle the rest
    } catch (error) {
      dispatch({
        type: 'AUTH_ERROR',
        payload: error instanceof Error ? error.message : 'Sign in failed',
      })
      throw error
    }
  }

  // Create user with email/password
  const createUserWithEmail = async (email: string, password: string) => {
    try {
      dispatch({ type: 'AUTH_LOADING' })
      await createUserWithEmailAndPassword(auth, email, password)
      // onAuthStateChanged will handle the rest
    } catch (error) {
      dispatch({
        type: 'AUTH_ERROR',
        payload: error instanceof Error ? error.message : 'Account creation failed',
      })
      throw error
    }
  }

  // Sign out
  const signOut = async () => {
    try {
      await firebaseSignOut(auth)
      dispatch({ type: 'AUTH_SIGNOUT' })
    } catch (error) {
      dispatch({
        type: 'AUTH_ERROR',
        payload: error instanceof Error ? error.message : 'Sign out failed',
      })
      throw error
    }
  }

  // Switch tenant
  const switchTenant = async (tenantId: string) => {
    const firebaseUser = auth.currentUser
    if (!firebaseUser) {
      throw new Error('Not authenticated')
    }

    const token = await firebaseUser.getIdToken()
    const response = await fetch(`${API_BASE_URL}/api/switch-tenant/${tenantId}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to switch tenant' }))
      throw new Error(error.detail || 'Failed to switch tenant')
    }

    // Reload to apply the new tenant context
    window.location.reload()
  }

  // Clear error
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' })
  }

  const value: AuthContextType = {
    ...state,
    signIn,
    signInWithEmail,
    createUserWithEmail,
    signOut,
    switchTenant,
    clearError,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
